import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Mail, MessageSquare, AlertCircle, Send, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import type { Database } from '@/integrations/supabase/types';  
import { PhoneNumberSelector } from './PhoneNumberSelector';

type Client = Database['public']['Tables']['clients']['Row'];

interface NotificationsTabProps {
  businessPortal: Client | null;
  onSettingsChange: (settings: any) => void;
}

export default function NotificationsTab({ businessPortal, onSettingsChange }: NotificationsTabProps) {
  const { apiKeys } = useAuth();
  const { toast } = useToast();
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  // Get current notification settings from business portal
  const notificationSettings = (businessPortal?.settings as any)?.portal?.notifications || {
    emailProvider: '',
    emailApiKey: '',
    fromName: '',
    fromEmail: '',
    smsProvider: 'twilio', // Force to twilio
    fromNumber: ''
  };

  // Check if Twilio is connected via API keys
  const isTwilioConnected = !!(apiKeys?.twilioSid && apiKeys?.twilioAuthToken);

  const updateSettings = (newSettings: any) => {
    const updatedSettings = {
      portal: {
        ...(businessPortal?.settings as any)?.portal,
        notifications: {
          ...notificationSettings,
          ...newSettings
        }
      }
    };
    onSettingsChange(updatedSettings);
  };

  const handleTestEmail = async () => {
    if (!testEmail || !notificationSettings.emailProvider) return;

    setTesting(true);
    try {
      const { error } = await supabase.functions.invoke('test-business-email', {
        body: {
          provider: notificationSettings.emailProvider,
          testEmail,
          subject: 'Test Email from Business Portal',
          body: 'This is a test email to verify your email provider configuration.',
          fromEmail: notificationSettings.fromEmail,
          fromName: notificationSettings.fromName
        }
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Test email sent successfully",
      });
    } catch (error: any) {
      console.error('Error sending test email:', error);
      toast({
        title: "Error",
        description: "Failed to send test email: " + error.message,
        variant: "destructive",
      });
    } finally {
      setTesting(false);
    }
  };

  const hasEmailProvider = !!notificationSettings.emailApiKey;
  const hasSmsProvider = isTwilioConnected && !!notificationSettings.fromNumber;

  return (
    <div className="space-y-6">
      {/* Email Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Notifications
          </CardTitle>
          <CardDescription>
            Configure email provider for automated client notifications
          </CardDescription>
          
          {/* Helper Links */}
          <div className="pt-2">
            <p className="text-sm text-muted-foreground mb-2">Need an email provider? Sign up here:</p>
            <div className="flex flex-wrap gap-2">
              <a 
                href="https://smtp2go.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                SMTP2GO <ExternalLink className="h-3 w-3" />
              </a>
              <a 
                href="https://sendgrid.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                SendGrid <ExternalLink className="h-3 w-3" />
              </a>
              <a 
                href="https://resend.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                Resend <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>

          {/* SMTP Configuration Notice */}
          <Alert className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              SMTP must be configured in order to enable client notifications
            </AlertDescription>
          </Alert>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email-provider">Email Provider</Label>
              <Select
                value={notificationSettings.emailProvider}
                onValueChange={(value) => updateSettings({ emailProvider: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select email provider" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="resend">Resend</SelectItem>
                  <SelectItem value="sendgrid">SendGrid</SelectItem>
                  <SelectItem value="smtp2go">SMTP2GO</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email-api-key">API Key</Label>
              <Input
                id="email-api-key"
                type="password"
                value={notificationSettings.emailApiKey}
                onChange={(e) => updateSettings({ emailApiKey: e.target.value })}
                placeholder="Enter API key"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="from-name">From Name</Label>
              <Input
                id="from-name"
                value={notificationSettings.fromName}
                onChange={(e) => updateSettings({ fromName: e.target.value })}
                placeholder="Your Company Name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="from-email">From Email</Label>
              <Input
                id="from-email"
                type="email"
                value={notificationSettings.fromEmail}
                onChange={(e) => updateSettings({ fromEmail: e.target.value })}
                placeholder="noreply@yourdomain.com"
              />
            </div>
          </div>

          {/* Test Email */}
          <div className="flex items-center gap-2 pt-4 border-t">
            <Input
              placeholder="test@example.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="flex-1"
            />
            <Button 
              onClick={handleTestEmail}
              disabled={testing || !hasEmailProvider || !testEmail}
              variant="outline"
            >
              <Send className="h-4 w-4 mr-2" />
              {testing ? 'Sending...' : 'Test Email'}
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Badge variant={hasEmailProvider ? 'default' : 'secondary'}>
              {hasEmailProvider ? 'Configured' : 'Not Configured'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* SMS Configuration - Hidden */}
      <Card className="hidden">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            SMS Notifications
          </CardTitle>
          <CardDescription>
            Configure SMS provider for urgent client notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!isTwilioConnected ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Twilio integration is required for SMS notifications. 
                <Link 
                  to="/api-settings" 
                  className="ml-1 inline-flex items-center gap-1 text-primary hover:underline"
                >
                  Configure Twilio in API Settings <ExternalLink className="h-3 w-3" />
                </Link>
              </AlertDescription>
            </Alert>
          ) : (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-green-600 dark:text-green-400">
                ✓ Twilio is connected and ready for SMS notifications
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sms-provider">SMS Provider</Label>
              <Select value="twilio" disabled>
                <SelectTrigger>
                  <SelectValue placeholder="Twilio" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="twilio">Twilio</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="from-number">From Number</Label>
              <PhoneNumberSelector
                value={notificationSettings.fromNumber}
                onChange={(phoneNumber) => updateSettings({ fromNumber: phoneNumber })}
                disabled={!isTwilioConnected}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Badge variant={hasSmsProvider ? 'default' : 'secondary'}>
              {hasSmsProvider ? 'Configured' : 'Not Configured'}
            </Badge>
            <Badge variant="outline">Beta</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Status Summary */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5" />
            <div>
              <div className="font-medium text-blue-700 dark:text-blue-300">
                Automated Client Notifications
              </div>
              <div className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                Notifications are automatically sent to clients when actionable items are detected in their assigned campaigns. 
                All notifications use your configured providers and maintain your white-label branding.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}